'use client'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { markAllAsRead } from './notification-actions'
import type { NotificationData } from './types'

interface NotificationPage {
  items: NotificationData[]
  nextCursor: string | null
  hasMore: boolean
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => markAllAsRead(),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications'] })
      await queryClient.cancelQueries({ queryKey: ['unread-count'] })

      const prevNotifications = queryClient.getQueriesData<{ pages: NotificationPage[]; pageParams: unknown[] }>({ queryKey: ['notifications'] })
      const prevCount = queryClient.getQueryData<number>(['unread-count'])

      // Optimistic: mark all as read
      const now = new Date().toISOString()
      queryClient.setQueriesData<{ pages: NotificationPage[]; pageParams: unknown[] }>(
        { queryKey: ['notifications'] },
        (old) => {
          if (!old) return old
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((n) =>
                n.readAt ? n : { ...n, readAt: now }
              ),
            })),
          }
        },
      )

      queryClient.setQueryData<number>(['unread-count'], 0)

      return { prevNotifications, prevCount }
    },
    onError: (_err, _vars, context) => {
      if (context?.prevNotifications) {
        for (const [key, data] of context.prevNotifications) {
          queryClient.setQueryData(key, data)
        }
      }
      if (context?.prevCount !== undefined) {
        queryClient.setQueryData(['unread-count'], context.prevCount)
      }
    },
  })
}
